import { dealerLabel } from "@/lib/naming";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useDealerContext } from "@/lib/useDealerContext";

const EMPTY_CLIENT = {
  recipient_name: "",
  company: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postal_code: "",
  phone: "",
  email: "",
  notify_client: false,
  delivery_instructions: "",
};

export function OrderSheetDialog({ open, onOpenChange, reservation = null, prefillLine = null, onCreated }) {
  const [qty, setQty] = useState(1);
  const [extraLines, setExtraLines] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [projects, setProjects] = useState([]);
  const [locations, setLocations] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [shipMode, setShipMode] = useState("default");
  const [locationId, setLocationId] = useState("");
  const [client, setClient] = useState(EMPTY_CLIENT);
  const [idemKey, setIdemKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [atp, setAtp] = useState(null); // RC52-INV-001 proactive shortage preview
  const { isManager, myLocationId, defaultLocationId, showLocationPicker, locLabel } = useDealerContext();
  const [bizLocationId, setBizLocationId] = useState("");

  useEffect(() => {
    if (open) {
      setQty(1);
      setExtraLines([]);
      setProjectId("");
      setShipMode("default");
      setLocationId("");
      setClient(EMPTY_CLIENT);
      setIdemKey(crypto.randomUUID());
      api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {});
      api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
      api.get("/dealer/profile").then((r) => {
        const locs = r.data.locations || [];
        setLocations(locs);
        if (locs.length === 0) setShipMode("client"); // no saved locations → only direct-to-client is valid
      }).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (open && !bizLocationId) setBizLocationId(isManager ? (myLocationId || "") : (defaultLocationId || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isManager, myLocationId, defaultLocationId]);

  const setClientField = (k) => (e) => setClient((c) => ({ ...c, [k]: e.target.value }));

  const composedLines = () => {
    const lines = [];
    if (!reservation && prefillLine) lines.push({ variant_id: prefillLine.variant_id, boxes: parseInt(qty, 10) || 0, ...(prefillLine.container_id ? { container_id: prefillLine.container_id } : {}) });
    for (const l of extraLines) if (l.variant_id) lines.push({ variant_id: l.variant_id, boxes: parseInt(l.boxes, 10) || 0 });
    return lines.filter((l) => l.boxes > 0);
  };

  useEffect(() => {
    if (!open || reservation) return undefined;
    const lines = composedLines();
    if (!lines.length) { setAtp(null); return undefined; }
    const t = setTimeout(() => {
      api.post("/orders/atp-preview", { lines }).then((r) => setAtp(r.data)).catch(() => setAtp(null));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, qty, extraLines, prefillLine, reservation]);

  const shipToPayload = () =>
    shipMode === "client"
      ? { direct_to_client: { recipient_name: client.recipient_name, company: client.company, phone: client.phone, email: client.email || null, notify_client: !!(client.notify_client && client.email), delivery_instructions: client.delivery_instructions,
          address: { line1: client.line1, line2: client.line2, city: client.city, state: client.state, postal_code: client.postal_code, country: "US" } } }
      : shipMode === "location" ? { location_id: locationId } : undefined;

  const createSplit = async () => {
    const plan = atp?.split_plan;
    if (!plan) return;
    setSaving(true);
    const created = [];
    try {
      for (const o of plan.orders) {
        const ship_to = shipToPayload();
        const { data } = await api.post("/orders", { lines: o.lines, split_group: plan.split_group, ...(projectId ? { project_id: projectId } : {}), ...(bizLocationId ? { location_id: bizLocationId } : {}), ...(ship_to ? { ship_to } : {}) },
          { headers: { "Idempotency-Key": `${idemKey}-${o.supply_location}-${o.container_id || "fl"}` } });
        created.push(data);
      }
      toast.success(`${created.length} linked orders created — ${created.map((c) => c.order_number).join(" + ")}`);
      onOpenChange(false);
      onCreated?.(created[0]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) + (created.length ? ` (${created.length} order(s) already created)` : ""));
      if (created.length) { onOpenChange(false); onCreated?.(created[0]); }
    } finally { setSaving(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const lines = [];
      if (!reservation && prefillLine) {
        lines.push({
          variant_id: prefillLine.variant_id,
          boxes: parseInt(qty, 10),
          ...(prefillLine.container_id ? { container_id: prefillLine.container_id } : {}),
        });
      }
      for (const l of extraLines) {
        if (l.variant_id) lines.push({ variant_id: l.variant_id, boxes: parseInt(l.boxes, 10) });
      }
      const ship_to =
        shipMode === "client"
          ? {
              direct_to_client: {
                recipient_name: client.recipient_name,
                company: client.company,
                phone: client.phone,
                email: client.email || null,
                notify_client: !!(client.notify_client && client.email),
                delivery_instructions: client.delivery_instructions,
                address: {
                  line1: client.line1,
                  line2: client.line2,
                  city: client.city,
                  state: client.state,
                  postal_code: client.postal_code,
                  country: "US",
                },
              },
            }
          : shipMode === "location"
            ? { location_id: locationId }
            : undefined;
      const payload = {
        ...(reservation ? { reservation_id: reservation.id } : {}),
        lines,
        ...(projectId ? { project_id: projectId } : {}),
        // Layer 1: business location attribution (≠ ship_to destination)
        ...(bizLocationId ? { location_id: bizLocationId } : {}),
        ...(ship_to ? { ship_to } : {}),
      };
      const { data } = await api.post("/orders", payload, { headers: { "Idempotency-Key": idemKey } });
      toast.success(`Order ${data.order_number} created — inventory held for checkout`);
      onOpenChange(false);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">New order</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">
            Inventory is committed immediately and held for 20 minutes while you complete checkout.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-2 space-y-5" data-testid="order-create-form">
          <div className="space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Lines</Label>
            {reservation && (
              <div className="rounded-md border border-border bg-background p-3 text-sm" data-testid="order-locked-line">
                <span className="font-medium text-charcoal">{dealerLabel(reservation)}</span>
                <span className="text-muted-foreground"> × {reservation.boxes} (reserved)</span>
              </div>
            )}
            {!reservation && prefillLine && (
              <div className="flex items-end gap-3" data-testid="order-line-0">
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium text-charcoal">
                    {prefillLine.label}
                    {prefillLine.container_number && (
                      <span className="ml-2 font-mono text-xs text-muted-foreground">incoming · {prefillLine.container_number}</span>
                    )}
                  </p>
                </div>
                <div className="w-24 space-y-1">
                  <Label className="text-xs text-warmgrey">Boxes</Label>
                  <Input type="number" min="1" required data-testid="order-qty-0" value={qty} onChange={(e) => setQty(e.target.value)} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
              </div>
            )}
            {extraLines.map((l, i) => (
              <div key={i} className="flex items-end gap-3" data-testid={`order-extra-line-${i}`}>
                <div className="flex-1">
                  <Select value={l.variant_id} onValueChange={(v) => setExtraLines((ls) => ls.map((x, j) => (j === i ? { ...x, variant_id: v } : x)))}>
                    <SelectTrigger data-testid={`order-extra-variant-${i}`} className="h-11 bg-white"><SelectValue placeholder="Select variant" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {catalog.map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.label}{v.color_code ? ` · ${v.color_code}` : ""}{v.sku ? ` · ${v.sku}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-24">
                  <Input type="number" min="1" data-testid={`order-extra-qty-${i}`} value={l.boxes} onChange={(e) => setExtraLines((ls) => ls.map((x, j) => (j === i ? { ...x, boxes: e.target.value } : x)))} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <Button type="button" variant="ghost" size="icon" data-testid={`order-extra-remove-${i}`} onClick={() => setExtraLines((ls) => ls.filter((_, j) => j !== i))} className="text-status-sold">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" data-testid="order-add-line" onClick={() => setExtraLines((ls) => [...ls, { variant_id: "", boxes: 1 }])} className="border-terracotta bg-terracotta/5 font-semibold text-terracotta hover:bg-terracotta/10">
              <Plus className="mr-1 h-3.5 w-3.5" /> Add another product (Florida stock)
            </Button>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Project (optional)</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="order-project-select" className="h-11 bg-white"><SelectValue placeholder="No project" /></SelectTrigger>
              <SelectContent className="bg-white">
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {isManager ? (
            <p className="text-xs text-muted-foreground" data-testid="order-location-fixed">
              Business location: {locLabel(myLocationId) || "your assigned location"} (assigned by Zelligio)
            </p>
          ) : showLocationPicker ? (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Business location</Label>
              <Select value={bizLocationId} onValueChange={setBizLocationId}>
                <SelectTrigger data-testid="order-biz-location-select" className="h-11 bg-white"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.label || l.type} — {l.address?.city}{l.address?.state ? `, ${l.address.state}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">The showroom responsible for this order — separate from the ship-to destination below.</p>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Ship to</Label>
            <Select value={shipMode} onValueChange={setShipMode}>
              <SelectTrigger data-testid="order-shipmode-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {locations.length > 0 && <SelectItem value="default">Default showroom</SelectItem>}
                {locations.length > 0 && <SelectItem value="location">Another saved location</SelectItem>}
                <SelectItem value="client">Direct to client / designer</SelectItem>
              </SelectContent>
            </Select>
            {shipMode === "location" && (
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger data-testid="order-location-select" className="h-11 bg-white"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.label || l.type} — {l.address?.city}{l.address?.state ? `, ${l.address.state}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {shipMode === "client" && (
              <div className="space-y-3 rounded-md border border-border bg-background p-4" data-testid="order-client-form">
                <Input required placeholder="Recipient name *" data-testid="order-client-name" value={client.recipient_name} onChange={setClientField("recipient_name")} className="h-11 bg-white focus-visible:ring-terracotta" />
                <Input placeholder="Company" data-testid="order-client-company" value={client.company} onChange={setClientField("company")} className="h-11 bg-white focus-visible:ring-terracotta" />
                <Input required placeholder="Address line 1 *" data-testid="order-client-line1" value={client.line1} onChange={setClientField("line1")} className="h-11 bg-white focus-visible:ring-terracotta" />
                <Input placeholder="Address line 2" data-testid="order-client-line2" value={client.line2} onChange={setClientField("line2")} className="h-11 bg-white focus-visible:ring-terracotta" />
                <div className="grid grid-cols-3 gap-3">
                  <Input required placeholder="City *" data-testid="order-client-city" value={client.city} onChange={setClientField("city")} className="h-11 bg-white focus-visible:ring-terracotta" />
                  <Input placeholder="State" data-testid="order-client-state" value={client.state} onChange={setClientField("state")} className="h-11 bg-white focus-visible:ring-terracotta" />
                  <Input placeholder="ZIP" data-testid="order-client-postal" value={client.postal_code} onChange={setClientField("postal_code")} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <Input placeholder="Phone" data-testid="order-client-phone" value={client.phone} onChange={setClientField("phone")} className="h-11 bg-white focus-visible:ring-terracotta" />
                <Input type="email" placeholder="Client email (optional)" data-testid="order-client-email" value={client.email} onChange={setClientField("email")} className="h-11 bg-white focus-visible:ring-terracotta" />
                <label className="flex items-center gap-2 text-sm text-charcoal" data-testid="order-client-notify-label">
                  <input type="checkbox" data-testid="order-client-notify" checked={!!client.notify_client} disabled={!client.email}
                    onChange={(e) => setClient((c) => ({ ...c, notify_client: e.target.checked }))} className="h-4 w-4 accent-terracotta" />
                  Email the client a shipping notification when this order ships (only with your authorization)
                </label>
                <Textarea placeholder="Delivery instructions (dock hours, gate code, call ahead…)" rows={3} data-testid="order-client-instructions" value={client.delivery_instructions} onChange={setClientField("delivery_instructions")} className="bg-white focus-visible:ring-terracotta" />
              </div>
            )}
          </div>

          {atp?.has_shortage && (
            <div className="rounded-md border border-terracotta/40 bg-terracotta/5 p-4 text-sm" data-testid="atp-shortage-panel">
              <p className="font-medium text-charcoal">Not enough Florida stock for the full quantity</p>
              <ul className="mt-1 space-y-0.5 text-xs text-charcoal">
                {atp.lines.filter((l) => l.shortage > 0).map((l) => (
                  <li key={l.variant_id} data-testid={`atp-shortage-${l.variant_id}`}>{l.sku}: {l.available} of {l.requested} boxes available now in Florida · short {l.shortage}
                    {l.alternatives.length > 0 && <span className="text-muted-foreground"> — {l.alternatives.map((a) => `${a.available} on ${a.reference || "incoming container"} (${a.source === "IN_TRANSIT" ? "in transit" : "Morocco"}${a.warehouse_eta ? `, ETA ${a.warehouse_eta}` : ""})`).join("; ")}</span>}
                  </li>
                ))}
              </ul>
              {atp.split_plan ? (
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground">Keep the full requirement by splitting into {atp.split_plan.orders.length} linked orders (one supply location each):</p>
                  <ul className="mt-1 text-xs text-charcoal">
                    {atp.split_plan.orders.map((o, i) => <li key={i} data-testid={`atp-split-order-${i}`}>• {o.supply_location === "FLORIDA" ? "Florida stock" : `${o.reference || "Container"} (${o.supply_location === "IN_TRANSIT" ? "in transit" : "Morocco"}${o.warehouse_eta ? `, ETA ${o.warehouse_eta}` : ""})`}: {o.lines.map((l) => `${l.boxes} boxes`).join(", ")} · {o.terms}</li>)}
                  </ul>
                  <Button type="button" onClick={createSplit} disabled={saving || (shipMode === "location" && !locationId)} data-testid="atp-create-split" className="mt-2 h-10 w-full bg-charcoal text-white hover:bg-charcoal/90">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create linked split orders"}
                  </Button>
                </div>
              ) : (
                <p className="mt-2 text-xs text-terracotta" data-testid="atp-unsourced">{atp.note}</p>
              )}
            </div>
          )}
          {(reservation ? reservation.source === "FLORIDA" : !prefillLine?.container_id) && (
            <p className="rounded-md border border-terracotta/40 bg-terracotta/5 p-3 text-xs leading-relaxed text-charcoal" data-testid="order-payment-window-warning">
              Creating the order starts a <strong>20-minute payment window</strong>{reservation ? " and replaces your 72-hour reservation" : ""}. If payment is not completed within 20 minutes, the boxes are released back to available stock.
            </p>
          )}
          <Button type="submit" disabled={saving || (shipMode === "location" && !locationId) || !!atp?.has_shortage} data-testid="order-create-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create order & hold inventory"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
